import { css } from "@emotion/react";
import styled from "@emotion/styled";

import { setResponsiveVariantStyles, theme, transientOptions } from "shared/lib/styles";
import type { ResponsiveVariants } from "shared/lib/styles";

export type StyledProps = {
  $variant:
    | { $aspectRatio: number; $type: "aspectRatio" }
    | { $aspectRatio: number; $type: "video" }
    | { $blockSize: string; $type: "static" }
    | { $height: number; $type: "facebook"; $width: number }
    | { $height: string; $type: "multimedia" }
    | { $responsiveBlockSize: ResponsiveVariants<string>; $type: "responsive" }
    | { $type: "datawrapper" };
};

export const WrapperContainer = styled("div", {
  ...transientOptions,
})<StyledProps>`
  ${props => {
    const variant = props.$variant;

    switch (variant.$type) {
      case "facebook":
        return css`
          margin-block-end: 20px;

          ${theme.breakpoints.down(540)} {
            --container-height: calc(
              var(--content-container-width) * ${variant.$height} / ${variant.$width} * 1px
            );

            block-size: var(--container-height);
          }
        `;
    }
  }}
`;

export const Container = styled("div", {
  ...transientOptions,
})<StyledProps>`
  display: grid;

  ${props => {
    const variant = props.$variant;

    switch (variant.$type) {
      case "aspectRatio":
        return css`
          inline-size: 100%;
          block-size: auto;
          aspect-ratio: ${variant.$aspectRatio};

          /* set style for portrait */
          ${variant.$aspectRatio < 1 &&
          css`
            place-self: center;

            max-inline-size: 476px;
          `}
        `;
      case "datawrapper":
        return css`
          inline-size: 100%;
        `;
      case "facebook":
        return css`
          inline-size: ${`${variant.$width}px`};
          block-size: ${`${variant.$height}px`};
          aspect-ratio: ${variant.$width / variant.$height};

          margin-inline: auto;

          ${theme.breakpoints.down(540)} {
            --scale-factor: calc(var(--content-container-width) / ${variant.$width});

            transform: scale(var(--scale-factor));
            transform-origin: top left;
          }
        `;
      case "responsive":
        return css`
          inline-size: 100%;
          ${setResponsiveVariantStyles(
            variant.$responsiveBlockSize,
            blockSize => css`
              block-size: ${blockSize};
            `,
          )}
        `;
      case "static":
        return css`
          inline-size: 100%;
          block-size: ${variant.$blockSize};
        `;
      case "video":
        return css`
          aspect-ratio: ${variant.$aspectRatio};

          ${variant.$aspectRatio < 1
            ? css`
                block-size: auto;
                max-inline-size: 476px;
                margin-inline: auto;
              `
            : css`
                inline-size: 100%;
                block-size: auto;
              `}
        `;
    }
  }}
`;

type StyledIframeProps = {
  $fitContainer?: boolean;
};

export const StyledIframe = styled("iframe", {
  ...transientOptions,
})<StyledIframeProps>`
  inline-size: 100%;

  ${props =>
    props.$fitContainer &&
    css`
      min-inline-size: 100%;
      min-block-size: 100%;
    `}
`;

type StyledYoutubeEmbedContainerProps = {
  $aspectRatio: number;
  $previewImage?: string;
};

export const StyledYoutubeEmbedContainer = styled("div", {
  ...transientOptions,
})<StyledYoutubeEmbedContainerProps>`
  display: flex;
  justify-content: center;

  inline-size: 100%;
  max-inline-size: ${props => (props.$aspectRatio < 1 ? "476px" : "auto")};
  margin-inline: auto;

  background-image: url(${props => props.$previewImage});
  background-repeat: no-repeat;
  background-position: center;
  background-size: auto 100%;
  aspect-ratio: ${props => props.$aspectRatio};

  /* CSpell:ignore ntpc */
  [data-ntpc="YouTubeEmbed"],
  lite-youtube {
    inline-size: 100% !important;
    block-size: 100% !important;
  }
`;
